import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, Phone, Mail, MapPin, Calendar, CreditCard } from "lucide-react";

const CheckoutPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    city: "",
    state: "",
    zip: "",
    address: "",
    deliveryDate: "",
    paymentMethod: "",
  });

  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Load cart from localStorage
    const cartData = localStorage.getItem("cart");
    if (cartData) {
      setCartItems(JSON.parse(cartData));
    }

    // Load customer data if user is logged in
    const customerProfile = localStorage.getItem("customerProfile");
    if (customerProfile) {
      try {
        const customer = JSON.parse(customerProfile);
        const shippingAddress = customer.shippingAddress;
        // Populate form with customer data
        setFormData(prevData => ({
          ...prevData,
          name: customer.billingPerson?.name || prevData.name,
          email: customer.email || prevData.email,
          city: shippingAddress?.city || prevData.city,
          state: shippingAddress?.state || prevData.state,
          zip: shippingAddress?.postalCode || prevData.zip,
          address: shippingAddress?.street || prevData.address,
        }));
      } catch (e) {
        console.error("Error parsing customer data:", e);
      }
    }

    // Load previously saved checkout data if exists
    const savedCheckoutData = localStorage.getItem("customerDetails");
    if (savedCheckoutData) {
      try {
        setFormData(prevData => ({
          ...prevData,
          ...JSON.parse(savedCheckoutData)
        }));
      } catch (e) {
        console.error("Error parsing saved checkout data:", e);
      }
    }
  }, []);

  const totalAmount = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveAndContinue = () => {
    if (!formData.name || !formData.mobile || !formData.address) {
      alert("Please fill required fields!");
      return;
    }
    localStorage.setItem("customerDetails", JSON.stringify(formData));
    localStorage.setItem("cart", JSON.stringify(cartItems));
    navigate("/payment"); // go to payment page
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Checkout
          </h1>
          <p className="text-gray-600">
            Complete your purchase with secure checkout
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Side - Delivery Form */}
          <div className="bg-white rounded-2xl shadow-lg p-5 border border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#82b016] p-2 rounded-lg">
                <User className="text-white" size={20} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">
                Delivery Information
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Name Field */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="text-gray-400" size={18} />
                  </div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#82b016] focus:border-[#82b016] transition-colors"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Mobile Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="text-gray-400" size={18} />
                  </div>
                  <input
                    type="text"
                    name="mobile"
                    placeholder="Enter mobile number"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#82b016] focus:border-[#82b016] transition-colors"
                    value={formData.mobile}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="text-gray-400" size={18} />
                  </div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#82b016] focus:border-[#82b016] transition-colors"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* City Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin className="text-gray-400" size={18} />
                  </div>
                  <input
                    type="text"
                    name="city"
                    placeholder="Enter city"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#82b016] focus:border-[#82b016] transition-colors"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* State Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin className="text-gray-400" size={18} />
                  </div>
                  <input
                    type="text"
                    name="state"
                    placeholder="Enter state"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#82b016] focus:border-[#82b016] transition-colors"
                    value={formData.state}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* ZIP Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP Code
                </label>
                <input
                  type="text"
                  name="zip"
                  placeholder="Enter ZIP code"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#82b016] focus:border-[#82b016] transition-colors"
                  value={formData.zip}
                  onChange={handleChange}
                />
              </div>

              {/* Address Field */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delivery Address *
                </label>
                <div className="relative">
                  <div className="absolute top-3 left-3">
                    <MapPin className="text-gray-400" size={18} />
                  </div>
                  <textarea
                    name="address"
                    placeholder="Enter your complete address"
                    rows="3"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#82b016] focus:border-[#82b016] transition-colors resize-none"
                    value={formData.address}
                    onChange={handleChange}
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Delivery Date */}
            <div className="mt-6">
              <label className=" text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                <Calendar className="text-gray-500" size={18} />
                Schedule Delivery
              </label>
              <input
                type="date"
                name="deliveryDate"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#82b016] focus:border-[#82b016] transition-colors"
                value={formData.deliveryDate}
                onChange={handleChange}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            <button
              onClick={handleSaveAndContinue}
              className="mt-8 w-full bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-white font-bold py-4 rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl flex items-center justify-center gap-2"
            >
              <CreditCard size={20} />
              Save & Continue to Payment
            </button>
          </div>

          {/* Right Side - Order Summary */}
          <div className="bg-white rounded-2xl shadow-lg p-5 border border-gray-200 sticky top-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#82b016] p-2 rounded-lg">
                <CreditCard className="text-white" size={20} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">
                Order Summary
              </h2>
            </div>

            <div className="space-y-4 mb-6 h-[400px] overflow-y-auto">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center py-2 px-3 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={item.imageUrl || "/assets/No-img.png.png"}
                      alt={item.name}
                      className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                    />
                    <div>
                      <p className="font-medium text-gray-800">{item.name}</p>
                      <p className="text-sm text-gray-500">
                        Qty: {item.quantity}
                      </p>
                    </div>
                  </div>
                  <span className="font-bold text-gray-800">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            <div className="bg-gray-100 border border-gray-200 p-4 space-y-3 rounded-lg">
              <div className="flex justify-between text-gray-900">
                <span>Subtotal</span>
                <span>${totalAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-900">
                <span>Shipping</span>
                <span className="text-green-600">Free</span>
              </div>
              <div className="flex justify-between text-gray-900">
                <span>Tax</span>
                <span>$0.00</span>
              </div>
              <div className="bg-gradient-to-r from-gray-100 to-gray-300 flex justify-between font-bold text-lg px-3 py-2 border border-gray-300 rounded-lg">
                <span>Total</span>
                <span className="text-gray-900">${totalAmount.toFixed(2)}</span>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="mt-5">
              <div className="grid grid-cols-2 gap-4 mx-2 font-bold text-gray-500 border border-gray-300 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm">Secure Payment</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm ">30-Day Returns</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm">Fast Delivery</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm">24/7 Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;